/**
 * localMetricsShared.ts — Helpers y tipos internos compartidos del cálculo de
 * métricas locales.
 *
 * QUÉ HACE: centraliza lecturas de storage (ventas, movimientos, pedidos),
 *   normalización de fechas, cortes por periodo, nombres/índices de días de la
 *   semana y colores de categorías usados por 2+ módulos del split de
 *   `localMetricsFromStorage`.
 *
 * QUÉ INVOCA: txRead (transactionalStore) y hasUserRealCajaActivity (caja).
 *
 * QUÉ EXPORTA: isoDay, pointRange, cap, VentaLocal, readVentasLocal, ventaFecha,
 *   ventasActivas, ChartPeriod, MovLocal, PedidoLocal, readMovimientosLocal,
 *   readPedidosLocal, movFecha, pedidoFecha, movimientosGastos,
 *   movimientosEntradas, movimientosSalidas, pedidosActivos,
 *   PeakHoursPeriod, BUSINESS_HOURS, peakHourCutoff, PeriodFilterRange,
 *   periodFilterRange, withinPeriod, WEEKDAY_NAMES,
 *   WEEKDAY_SHORT, weekdayIndex, CATEGORY_COLORS, EXTRA_COLORS, categoryColor,
 *   DonutPeriodLike, DrillLevel, ventasCutoffForPeriod.
 *
 * QUÉ NO DEBE HACER: UI, HTTP ni cálculos de métricas finales.
 */
import { txRead } from "@/lib/persistence/transactionalStore";
import { defaultProducts } from "@/mocks/data/products";
import { hasUserRealCajaActivity } from "@/features/caja/services/caja.service";
import { startOfDay, subDays } from "date-fns";
import { resolvePeriodWindow } from "@/lib/utils/periodWindows";
import type { ChartDataPoint } from "@/lib/utils/chart-data";

/** Fecha en formato YYYY-MM-DD (local) — para deep-links de detalle */
export const isoDay = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

/** Rango inclusivo (desde–hasta) que representa un punto para deep-links de detalle */
export const pointRange = (
  desde: Date,
  hasta: Date,
): Pick<ChartDataPoint, "periodDesde" | "periodHasta"> => ({
  periodDesde: isoDay(desde),
  periodHasta: isoDay(hasta),
});

/** Capitaliza la primera letra de una cadena */
export const cap = (s: string): string =>
  s.charAt(0).toUpperCase() + s.slice(1);

export interface VentaLocal {
  id: string;
  total: number;
  createdAt?: string;
  fecha?: string;
  estado?: string;
  anulada?: boolean;
  empleadoNombre?: string;
  userName?: string;
  /** ID inmutable del método de pago principal (o "mixto"). */
  metodoPago?: string;
  /** Etiqueta snapshot del método principal al cobrar (anti-datos fantasma). */
  metodoPagoLabel?: string;
  /** Desglose de cobro cuando la venta es mixta (varios métodos). */
  detalleMetodoPago?: {
    breakdown?: Array<{ method: string; amount: number; methodLabel?: string }>;
  };
  productos?: Array<{
    nombre?: string;
    productoId?: string;
    cantidad?: number;
    precioUnitario?: number;
    subtotal?: number;
    /** IVA incluido de la línea (para el recaudo de impuestos). */
    tieneIVA?: boolean;
    porcentajeIVA?: number;
    modificadoresSeleccionados?: Array<{
      optionId: string;
      optionLabel: string;
    }>;
    modifierKey?: string;
  }>;
  /** Código/cédula del cliente de puntos (para recurrencia de clientes). */
  clientePuntosCodigo?: string;
  clientePuntosCedula?: string;
  /** Snapshot de descuentos aplicados al cobrar (para descuentos del equipo). */
  descuentos?: {
    totalDescuentoItems?: number;
    descuentoGlobal?: number;
  };
  /** Propina/servicio cobrado en la venta (COP). */
  servicioAmount?: number;
  /** Canal de origen de la venta ("caja" por defecto, "mesas" desde salón). */
  canal?: "caja" | "mesas";
  /** Punto de venta (multi-caja) al que pertenece la venta. */
  puntoCajaNombre?: string;
}

export function readVentasLocal(): VentaLocal[] {
  const raw = txRead("caja_ventas_historial", []);
  return Array.isArray(raw) ? (raw as VentaLocal[]) : [];
}

/** Producto local con los campos que consumen las métricas (lector compartido). */
export interface ProductoLocal {
  id?: string;
  nombre?: string;
  categoria?: string;
  costo?: number;
  precio?: number;
  stock?: number;
  stockMinimo?: number;
  isActive?: boolean;
  tieneVencimiento?: boolean;
  fechaVencimiento?: string;
}

/**
 * Lee `inventory_products` con fallback a `defaultProducts`. Único punto de
 * lectura de productos para los resolvers de Métricas (evita duplicar el
 * fallback y la forma del dato en cada archivo).
 */
export function readProductosLocal(): ProductoLocal[] {
  const raw = txRead("inventory_products", defaultProducts);
  return Array.isArray(raw) && raw.length > 0
    ? (raw as ProductoLocal[])
    : (defaultProducts as ProductoLocal[]);
}

export function ventaFecha(v: VentaLocal): Date {
  const iso = v.createdAt || v.fecha;
  return iso ? new Date(iso) : new Date();
}

export function ventasActivas(list: VentaLocal[]): VentaLocal[] {
  return list.filter((v) => v.estado !== "anulada" && !v.anulada);
}

/** Tipo de periodo del gráfico (idéntico a PeriodOption de InteractiveBarChart) */
export type ChartPeriod = "hoy" | "semana" | "mes" | "anio";

export interface MovLocal {
  tipo: string;
  monto: number;
  fecha: string;
  estado?: string;
  /** Categoría/motivo del movimiento (base de "Gastos por categoría"). */
  motivo?: string;
  /** Ingreso por abono a una cuenta de crédito (cobro de deuda, no venta POS). */
  creditoAbono?: boolean;
}

export interface PedidoLocal {
  id?: string;
  total: number;
  fechaCreacion: string;
  /** Última actualización del pedido (cambio de estado); base de "despachados hoy". */
  fechaActualizacion?: string;
  estado?: string;
  /** Canal de entrega: "local" (mostrador/mesa) o "domicilio". */
  tipoEntrega?: "domicilio" | "local";
  /** Marcas de tiempo por transición (base de tiempos reales de preparación). */
  confirmadoEn?: string;
  preparandoEn?: string;
  listoEn?: string;
  entregadoEn?: string;
  /** Ítems del pedido — usados para calcular el costo variable (COGS) web. */
  items?: Array<{ productoId?: string; cantidad?: number }>;
}

export function readMovimientosLocal(): MovLocal[] {
  const raw = txRead("caja_movimientos", []);
  return Array.isArray(raw) ? (raw as MovLocal[]) : [];
}

/**
 * Lee pedidos de tienda_pedidos excluyendo los pedidos demo seeded (id: 'ped_demo_*').
 * Evita que el gráfico muestre ventas de tienda falsas antes de pedidos reales.
 */
export function readPedidosLocal(): PedidoLocal[] {
  const raw = txRead("tienda_pedidos", []);
  if (!Array.isArray(raw)) return [];
  const list = raw as PedidoLocal[];
  // Si el usuario ya tiene actividad real, filtra pedidos demo
  if (hasUserRealCajaActivity()) {
    return list.filter((p) => !p.id?.startsWith("ped_demo_"));
  }
  return list;
}

export function movFecha(m: MovLocal): Date {
  return m.fecha ? new Date(m.fecha) : new Date();
}

export function pedidoFecha(p: PedidoLocal): Date {
  return p.fechaCreacion ? new Date(p.fechaCreacion) : new Date();
}

export function movimientosGastos(list: MovLocal[]): MovLocal[] {
  return list.filter(
    (m) =>
      (m.tipo === "gasto" || m.tipo === "retiro") && m.estado !== "anulado",
  );
}

/** Entradas de dinero a caja: ingresos manuales y abonos de crédito. */
export function movimientosEntradas(list: MovLocal[]): MovLocal[] {
  return list.filter((m) => m.tipo === "ingreso" && m.estado !== "anulado");
}

/** Salidas de dinero de caja: gastos, retiros y devoluciones. */
export function movimientosSalidas(list: MovLocal[]): MovLocal[] {
  return list.filter(
    (m) =>
      (m.tipo === "gasto" || m.tipo === "retiro" || m.tipo === "devolucion") &&
      m.estado !== "anulado",
  );
}

export function pedidosActivos(list: PedidoLocal[]): PedidoLocal[] {
  return list.filter((p) => p.estado !== "cancelado");
}

export type PeakHoursPeriod = "hoy" | "semana" | "mes" | "anio";

export const BUSINESS_HOURS = Array.from({ length: 24 }, (_, h) => h);

export function peakHourCutoff(period: PeakHoursPeriod): Date {
  const now = startOfDay(new Date());
  if (period === "hoy") return now;
  if (period === "semana") return subDays(now, 7);
  if (period === "mes") return subDays(now, 30);
  return subDays(now, 365);
}

/** Rango de filtro de un periodo: rolling (sin fecha) o calendario (con fecha). */
export interface PeriodFilterRange {
  from: Date;
  /** `null` = sin límite superior (ventana rolling hasta ahora). */
  to: Date | null;
}

/**
 * Rango para filtrar datos locales de un periodo. Sin fecha de referencia
 * mantiene la ventana rolling histórica (7/30/365 días); con fecha usa la
 * ventana **calendario** (día, semana Lun→Dom, mes o año) que permite navegar
 * ‹ › entre periodos.
 */
export function periodFilterRange(
  period: PeakHoursPeriod,
  referenceDate?: Date,
): PeriodFilterRange {
  if (referenceDate) return resolvePeriodWindow(period, referenceDate);
  return { from: peakHourCutoff(period), to: null };
}

/** true si la fecha cae dentro del rango de filtro. */
export function withinPeriod(date: Date, range: PeriodFilterRange): boolean {
  return (
    date.getTime() >= range.from.getTime() &&
    (range.to === null || date.getTime() <= range.to.getTime())
  );
}

/** Nombres de los días en orden de negocio (0 = Lunes … 6 = Domingo). */
export const WEEKDAY_NAMES = [
  "Lunes",
  "Martes",
  "Miércoles",
  "Jueves",
  "Viernes",
  "Sábado",
  "Domingo",
];
export const WEEKDAY_SHORT = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];

/** Índice de día de la semana con Lunes = 0 … Domingo = 6. */
export function weekdayIndex(date: Date): number {
  return (date.getDay() + 6) % 7;
}

/** Colores deterministas por nombre de categoría */
export const CATEGORY_COLORS: Record<string, string> = {
  Bebidas: "#3b82f6",
  Panadería: "#10b981",
  Snacks: "#8b5cf6",
  Postres: "#f59e0b",
  Lácteos: "#06b6d4",
  Carnes: "#ef4444",
  Frutas: "#84cc16",
  Verduras: "#22c55e",
  Otros: "#94a3b8",
};
export const EXTRA_COLORS = [
  "#ec4899",
  "#f97316",
  "#a3e635",
  "#2dd4bf",
  "#818cf8",
  "#fb7185",
];

export function categoryColor(name: string, fallbackIdx: number): string {
  return (
    CATEGORY_COLORS[name] ?? EXTRA_COLORS[fallbackIdx % EXTRA_COLORS.length]
  );
}

export type DonutPeriodLike = "hoy" | "semana" | "mes" | "anio";
export type DrillLevel = "hora" | "hoy" | "mes" | "anio";

export function ventasCutoffForPeriod(period: DonutPeriodLike): Date {
  const now = startOfDay(new Date());
  if (period === "hoy") return now;
  if (period === "semana") return subDays(now, 7);
  if (period === "mes") return subDays(now, 30);
  return subDays(now, 365);
}
